/**
 * Money-valued fiscal parameters of a jurisdiction (#143): the stamp-duty
 * scale (CGI art. 519). The code TICKET_INVOICE_CEILING stays readable for
 * the rows already stored and synced; no jurisdiction declares it and no
 * rule reads it.
 *
 * Each is a dated parameter, stored in the platform table
 * `FiscalParameterPeriod`: a value in a currency, in force from its
 * `effectiveFrom` day on, until a later active row replaces it. The law
 * that changes the currency sets new amounts in it; the software reads
 * them as data. `fiscalParameterAt` resolves a day and fails closed: a day
 * no active row covers, or a value in a currency other than the
 * document's, is refused, never converted.
 *
 * Days are UTC calendar days; the registry admits only jurisdictions at
 * UTC+0 all year.
 */
import { InvoiceError } from "../errors/index.js";
import { JURISDICTIONS } from "../jurisdictions/registry.js";
import { currencyDay } from "./certification-currency.js";
import { currencyCode, toCentimes } from "./money.js";
/** The parameter codes of `FiscalParameterPeriod.code`. */
export const FISCAL_PARAMETER_CODES = {
    /** Receipt stamp duty scale, CGI art. 519. */
    STAMP_DUTY_SCALE: "STAMP_DUTY_SCALE",
    /** Stored rows only: no jurisdiction declares it and no rule reads it. */
    TICKET_INVOICE_CEILING: "TICKET_INVOICE_CEILING",
};
/** The art. 519 scale in franc CFA. */
const STAMP_DUTY_SCALE_ART_519 = Object.freeze({
    minAmount: 1_000,
    bands: Object.freeze([
        Object.freeze({ upTo: 5_000, duty: 30 }),
        Object.freeze({ upTo: 10_000, duty: 50 }),
        Object.freeze({ upTo: 50_000, duty: 100 }),
    ]),
    beyond: Object.freeze({ fraction: 50_000, perFraction: 50 }),
});
/**
 * The first rows of Burkina Faso, seeded by
 * `packages/api/prisma/sql/fiscal-parameter-guards.sql` in every database
 * and by the mcf-agent in its own store. A test keeps them identical.
 */
export const FISCAL_PARAMETER_ORIGINS = Object.freeze([
    Object.freeze({
        id: "fiscal-parameter-bf-stamp-duty-origin",
        jurisdiction: JURISDICTIONS["BF"].code,
        code: FISCAL_PARAMETER_CODES.STAMP_DUTY_SCALE,
        currency: "XOF",
        value: STAMP_DUTY_SCALE_ART_519,
        effectiveFrom: "1900-01-01",
        legalReference: "CGI art. 519 : 30 F de 1 000 à 5 000, 50 F de 5 001 à 10 000, 100 F de 10 001 à 50 000, au-delà 50 F en sus par fraction de 50 000",
        createdById: "system",
    }),
    Object.freeze({
        id: "fiscal-parameter-bf-ticket-ceiling-origin",
        jurisdiction: JURISDICTIONS["BF"].code,
        code: FISCAL_PARAMETER_CODES.TICKET_INVOICE_CEILING,
        currency: "XOF",
        value: Object.freeze({ amount: 50_000 }),
        effectiveFrom: "1900-01-01",
        legalReference: "Spécifications techniques des SFE (DGI), § 7.3 : plafond des factures d'acompte FT et ET",
        createdById: "system",
    }),
]);
/** The art. 519 scale in franc CFA, in force from the origin row on. */
export const STAMP_DUTY_ORIGIN = Object.freeze({
    code: FISCAL_PARAMETER_CODES.STAMP_DUTY_SCALE,
    currency: "XOF",
    value: STAMP_DUTY_SCALE_ART_519,
    effectiveFrom: "1900-01-01",
});
/** The stored origin row of TICKET_INVOICE_CEILING; no rule reads it. */
export const TICKET_INVOICE_CEILING_ORIGIN = Object.freeze({
    code: FISCAL_PARAMETER_CODES.TICKET_INVOICE_CEILING,
    currency: "XOF",
    value: FISCAL_PARAMETER_ORIGINS[1].value,
    effectiveFrom: "1900-01-01",
});
function isPositiveAmount(value) {
    return typeof value === "number" && Number.isFinite(value) && value > 0 && Math.abs(toCentimes(value) - value * 100) < 1e-6;
}
function isObject(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** True for a known parameter code. */
export function isFiscalParameterCode(value) {
    return Object.values(FISCAL_PARAMETER_CODES).includes(value);
}
/**
 * Why `value` is not a valid value of `code`, or null. Amounts are positive
 * and carry at most two decimals; a scale's bands rise strictly, start above
 * its minimum and have non-decreasing duties.
 */
export function fiscalParameterValueError(code, value) {
    if (code === FISCAL_PARAMETER_CODES.TICKET_INVOICE_CEILING) {
        if (!isObject(value) || !isPositiveAmount(value["amount"])) {
            return "Le plafond doit être un montant positif (au plus deux décimales) : { amount }";
        }
        return Object.keys(value).length === 1 ? null : "Le plafond ne comporte que le champ amount";
    }
    if (code === FISCAL_PARAMETER_CODES.STAMP_DUTY_SCALE) {
        if (!isObject(value) || !isPositiveAmount(value["minAmount"])) {
            return "Le barème du timbre doit indiquer le montant à partir duquel le droit est dû (minAmount)";
        }
        const bands = value["bands"];
        if (!Array.isArray(bands) || bands.length === 0) {
            return "Le barème du timbre doit comporter au moins une tranche (bands : [{ upTo, duty }])";
        }
        let floor = value["minAmount"];
        let duty = 0;
        for (const [index, band] of bands.entries()) {
            if (!isObject(band) || !isPositiveAmount(band["upTo"]) || !isPositiveAmount(band["duty"])) {
                return `Tranche ${index + 1} du barème du timbre : upTo et duty doivent être des montants positifs`;
            }
            if (band["upTo"] < floor || band["duty"] < duty) {
                return `Tranche ${index + 1} du barème du timbre : les bornes et les droits doivent croître`;
            }
            floor = band["upTo"];
            duty = band["duty"];
        }
        const beyond = value["beyond"];
        if (!isObject(beyond) || !isPositiveAmount(beyond["fraction"]) || !isPositiveAmount(beyond["perFraction"])) {
            return "Le barème du timbre doit indiquer le droit en sus par fraction au-delà de la dernière tranche (beyond : { fraction, perFraction })";
        }
        return null;
    }
    return `Paramètre fiscal inconnu : « ${code} »`;
}
function periodStart(period) {
    return currencyDay(period.effectiveFrom);
}
/** Ids of the origin rows, in every database from its creation: known at any instant. */
const ORIGIN_IDS = new Set(FISCAL_PARAMETER_ORIGINS.map((origin) => origin.id));
function instant(value) {
    return new Date(value).getTime();
}
/**
 * Whether `period` is an active row: not withdrawn; with `knownAt`, in its
 * currency, recorded no later than its instant (an origin row or a literal
 * always is), and not withdrawn by then.
 */
function activeRow(period, knownAt) {
    if (knownAt === undefined)
        return period.withdrawnAt == null;
    const cut = instant(knownAt.recordedNoLaterThan);
    if (period.currency !== knownAt.currency)
        return false;
    const known = (period.id !== undefined && ORIGIN_IDS.has(period.id)) || period.createdAt == null || instant(period.createdAt) <= cut;
    return known && (period.withdrawnAt == null || instant(period.withdrawnAt) > cut);
}
/**
 * The active row of `code` in `jurisdiction` in force on the day of
 * `date`: the latest active row starting on or before it, among the rows
 * `knownAt` names when given (`FiscalParameterKnownAt`). Throws when none
 * covers the day, when two active rows start the same day, or when the row
 * holds an invalid value: no fiscal amount is applied on a guess.
 */
export function fiscalParameterAt(periods, jurisdiction, code, date, knownAt) {
    const day = currencyDay(date);
    let best = null;
    let tie = false;
    for (const period of periods) {
        if (period.jurisdiction !== jurisdiction || period.code !== code || !activeRow(period, knownAt))
            continue;
        const start = periodStart(period);
        if (start > day)
            continue;
        const bestStart = best === null ? null : periodStart(best);
        if (bestStart === null || start > bestStart) {
            best = period;
            tie = false;
        }
        else if (start === bestStart) {
            tie = true;
        }
    }
    if (best === null) {
        const known = knownAt === undefined
            ? ""
            : `, en ${knownAt.currency}, connue au ${new Date(knownAt.recordedNoLaterThan).toISOString()}`;
        throw new InvoiceError(`Aucune valeur du paramètre fiscal ${code} n'est en vigueur le ${day} (${jurisdiction})${known}`, "PARAMETRE_FISCAL_INDEFINI", 500);
    }
    if (tie) {
        throw new InvoiceError(`Deux valeurs actives du paramètre fiscal ${code} commencent le ${periodStart(best)}`, "PARAMETRE_FISCAL_AMBIGU", 500);
    }
    const error = fiscalParameterValueError(code, best.value);
    if (error !== null) {
        throw new InvoiceError(`Paramètre fiscal ${code} du ${periodStart(best)} invalide : ${error}`, "PARAMETRE_FISCAL_INVALIDE", 500);
    }
    return {
        currency: best.currency,
        value: best.value,
        effectiveFrom: periodStart(best),
    };
}
/** French name of a parameter in messages. */
const PARAMETER_NAMES = {
    STAMP_DUTY_SCALE: "Le barème du droit de timbre",
    TICKET_INVOICE_CEILING: "Le plafond des factures FT et ET",
};
/**
 * The value of `code` in force on the day of `date` for a document in
 * `currency`. Refuses a value stated in another currency: the law that
 * changes the currency states the amounts in the new one, and the software
 * never converts a fiscal amount. With `recordedNoLaterThan`, the value of
 * a document the MCF certified at that instant: among the rows in
 * `currency` as known then (`FiscalParameterKnownAt`).
 */
export function fiscalParameterForCurrency(periods, code, currency, date, jurisdiction, recordedNoLaterThan) {
    const row = fiscalParameterAt(periods, jurisdiction, code, date, recordedNoLaterThan === undefined ? undefined : { recordedNoLaterThan, currency: currencyCode(currency) });
    if (row.currency !== currencyCode(currency)) {
        throw new InvoiceError(`${PARAMETER_NAMES[code]} en vigueur le ${currencyDay(date)} est exprimé en ${row.currency} ; le document est en ${currencyCode(currency)}. Aucune valeur n'est publiée dans cette monnaie : elle doit l'être avant d'émettre.`, "PARAMETRE_FISCAL_AUTRE_MONNAIE", 400);
    }
    return { code, currency, value: row.value, effectiveFrom: row.effectiveFrom };
}
//# sourceMappingURL=fiscal-parameters.js.map